// ADR-0152 (fase 2, #0748) — avisos y guías de mantención por proyecto.
//
// La inmobiliaria publica un texto corto para TODO un proyecto (un
// `asset_group`): un aviso del edificio («Corte de agua programado») o una
// guía de mantención («Llegó la primavera: revisa ventanas y sellos»). Le
// llega a cada propietario con cuenta de una unidad del proyecto en la
// campana de la app (una fila por unidad) y por correo (uno por
// propietario). Es la excepción documentada a «el aviso no lleva texto del
// personal»: el título y el cuerpo completos viajan a la campana y al correo.
//
// Lado de la inmobiliaria (dashboard):
//   GET  /asset-groups/:id/announcements                         → lista + audiencia
//   POST /asset-groups/:id/announcements/attachment-upload-url   → presign PUT (PDF)
//   POST /asset-groups/:id/announcements                         → publica o programa
//                                                                  (Idempotency-Key)
//   POST /asset-groups/:id/announcements/:announcementId/retract → «Retirar»
//   POST /asset-groups/:id/announcements/:announcementId/cancel  → «Cancelar programación»
//   GET  /asset-groups/:id/announcements/:announcementId/attachment-url → presign GET
// Quién: `jefe_proyecto` en sus proyectos y `gerente_postventa` en toda la
// org (más el admin), igual que la bandeja de consultas. Un proyecto fuera
// del alcance responde 404, sin confirmar que exista.
//
// Lado del propietario (PWA): la fila de la campana (GET /me/notifications)
// trae el texto completo y `announcement` (retirado, adjunto). El PDF se
// abre con GET /me/notifications/:id/attachment-url.
//
// Un aviso publicado NO se edita: se retira (queda visible como
// «Retirado», nunca se borra) y, para corregir, se publica otro. Lo escrito
// puede leerse como oferta vinculante (ADR-0140): nada se borra.
import { ANNOUNCEMENT_CATEGORIES, announcementCategorySchema } from "@bitacorapro/core-engine";
import { z } from "zod";
import { DOCUMENT_CONTENT_TYPE, DOCUMENT_MAX_BYTES, documentDownloadUrlResponseSchema, documentUploadUrlResponseSchema, } from "./documents.js";
export { ANNOUNCEMENT_CATEGORIES, announcementCategorySchema };
/** Topes del texto (decisión del founder). Los CHECK de la tabla los
 *  repiten; un test los compara. Texto plano: la app y el correo lo
 *  pintan con los saltos de línea tal cual, nunca como HTML. */
export const ANNOUNCEMENT_TITLE_MAX = 80;
export const ANNOUNCEMENT_BODY_MAX = 600;
/** Lo más lejos que se puede programar un envío. */
export const ANNOUNCEMENT_SCHEDULE_MAX_DAYS = 90;
/** Un programado que no salió a su hora (el tick no corrió: el job
 *  apagado, una caída) sale igual si se atrasó a lo más esto. Pasado, se
 *  cancela solo (`overdue`): un «Corte de agua el sábado» que llega el
 *  lunes hace más daño que no llegar. */
export const ANNOUNCEMENT_SCHEDULE_MAX_LATENESS_MS = 2 * 60 * 60 * 1000;
/** Quién publica: el mismo par de roles que la bandeja de consultas. El
 *  alcance (qué proyectos) lo decide el servidor con `loadScopedAssets`. */
export const ANNOUNCEMENT_PUBLISHER_ROLES = ["jefe_proyecto", "gerente_postventa"];
export function isAnnouncementPublisherRole(role) {
    return ANNOUNCEMENT_PUBLISHER_ROLES.includes(role);
}
/** Los roles de plataforma con alcance de toda la org (el admin), que
 *  también entran a los avisos. El mismo conjunto que `ORG_SCOPED_ROLES`
 *  de core-api; un test los compara. */
export const ANNOUNCEMENT_ORG_WIDE_ROLES = ["admin", "company_manager"];
/** ¿Este rol gestiona avisos (publica, programa, retira, cancela)? La
 *  misma regla en el servidor (403 al resto), en la barra lateral del
 *  dashboard (sin entrada para el resto) y al publicar un programado (el
 *  autor tiene que seguir pudiendo). */
export function canManageAnnouncements(role) {
    return (isAnnouncementPublisherRole(role) ||
        ANNOUNCEMENT_ORG_WIDE_ROLES.includes(role));
}
/** Lo que se lee en el selector del formulario y en la lista. */
export const ANNOUNCEMENT_CATEGORY_LABEL = {
    aviso: "Aviso",
    mant: "Guía de mantención",
};
// ─── errores ─────────────────────────────────────────────────────────
/** 403 — el rol no publica avisos. */
export const ANNOUNCEMENTS_FORBIDDEN = "ANNOUNCEMENTS_FORBIDDEN";
/** 403 — la receta no tiene propietario externo (facility): no hay a
 *  quién avisar. */
export const ANNOUNCEMENTS_NOT_AVAILABLE = "ANNOUNCEMENTS_NOT_AVAILABLE";
/** 404 — el aviso no existe, es de otro proyecto o de otra org. */
export const ANNOUNCEMENT_NOT_FOUND = "ANNOUNCEMENT_NOT_FOUND";
/** 400 — la fecha programada está en el pasado o a más de 90 días. */
export const ANNOUNCEMENT_SCHEDULE_INVALID = "ANNOUNCEMENT_SCHEDULE_INVALID";
/** 422 — un envío inmediato a un proyecto sin propietarios con cuenta. */
export const ANNOUNCEMENT_NO_RECIPIENTS = "ANNOUNCEMENT_NO_RECIPIENTS";
/** 409 — retirar algo que no está publicado (o ya se retiró), o cancelar
 *  algo que ya salió (o ya se canceló). */
export const ANNOUNCEMENT_STATE_CONFLICT = "ANNOUNCEMENT_STATE_CONFLICT";
/** 422 — programar en un ambiente donde el job de avisos no corre (nada
 *  lo publicaría): sólo se puede enviar ahora. */
export const ANNOUNCEMENT_SCHEDULING_UNAVAILABLE = "ANNOUNCEMENT_SCHEDULING_UNAVAILABLE";
// ─── adjunto ─────────────────────────────────────────────────────────
/** Un PDF opcional por aviso, con las mismas reglas que un documento del
 *  proyecto (ADR-0144 §4): sólo PDF, hasta 10 MB, validado contra el
 *  objeto subido. */
export const ANNOUNCEMENT_ATTACHMENT_CONTENT_TYPE = DOCUMENT_CONTENT_TYPE;
export const ANNOUNCEMENT_ATTACHMENT_MAX_BYTES = DOCUMENT_MAX_BYTES;
export const announcementAttachmentUploadUrlRequestSchema = z
    .object({
    contentType: z.literal(ANNOUNCEMENT_ATTACHMENT_CONTENT_TYPE),
    contentLength: z.number().int().positive().max(ANNOUNCEMENT_ATTACHMENT_MAX_BYTES),
})
    .strict();
export const announcementAttachmentUploadUrlResponseSchema = documentUploadUrlResponseSchema;
export const announcementAttachmentUrlResponseSchema = documentDownloadUrlResponseSchema;
// ─── publicar ────────────────────────────────────────────────────────
/** Saltos de línea normalizados a `\n` y bordes recortados; el tope se
 *  mide sobre lo que se guarda. */
const plainText = (max) => z
    .string()
    .transform((s) => s.replace(/\r\n?/g, "\n").trim())
    .pipe(z.string().min(1).max(max));
export const announcementCreateRequestSchema = z
    .object({
    category: announcementCategorySchema,
    title: plainText(ANNOUNCEMENT_TITLE_MAX).refine((s) => !s.includes("\n"), {
        message: "El título va en una sola línea.",
    }),
    body: plainText(ANNOUNCEMENT_BODY_MAX),
    /** Envío programado (ISO con zona). Ausente o null = ahora. El servidor
     *  exige que esté en el futuro y a no más de 90 días. */
    scheduledFor: z.string().datetime({ offset: true }).nullable().optional(),
    /** El PDF ya subido con el presign de este proyecto. */
    attachment: z
        .object({ objectKey: z.string().min(1).max(512) })
        .strict()
        .nullable()
        .optional(),
})
    .strict();
// ─── lista (inmobiliaria) ────────────────────────────────────────────
/** El estado visible, derivado (nunca guardado):
 *  - `programado`: espera su fecha; se puede cancelar;
 *  - `cancelado`: se canceló antes de salir; no llegó a nadie;
 *  - `enviando`: publicado, quedan correos por enviar o reintentar;
 *  - `enviado`: publicado y sin correos pendientes;
 *  - `retirado`: publicado y retirado; el propietario lo ve tachado. */
export const ANNOUNCEMENT_STATUSES = [
    "programado",
    "cancelado",
    "enviando",
    "enviado",
    "retirado",
];
export const announcementStatusSchema = z.enum(ANNOUNCEMENT_STATUSES);
export const ANNOUNCEMENT_STATUS_LABEL = {
    programado: "Programado",
    cancelado: "Cancelado",
    enviando: "Enviando",
    enviado: "Enviado",
    retirado: "Retirado",
};
/** Por qué un programado no salió:
 *  - `staff`: lo canceló alguien del personal (`cancelledBy`);
 *  - `author_not_authorized`: al llegar su hora, quien lo programó ya no
 *    gestionaba avisos de este proyecto (cambió de rol, de proyecto o lo
 *    dieron de baja);
 *  - `no_recipients`: al llegar su hora el proyecto no tenía propietarios
 *    con cuenta activa;
 *  - `overdue`: no salió a su hora y se atrasó más de
 *    `ANNOUNCEMENT_SCHEDULE_MAX_LATENESS_MS`.
 *  Los tres últimos los decide el sistema (`cancelledBy` null). */
export const ANNOUNCEMENT_CANCEL_REASONS = [
    "staff",
    "author_not_authorized",
    "no_recipients",
    "overdue",
];
export const announcementCancelReasonSchema = z.enum(ANNOUNCEMENT_CANCEL_REASONS);
/** Los correos de un aviso publicado: uno por propietario. Cada correo cae
 *  en exactamente uno de los cinco grupos (suman `recipients`). */
export const announcementDeliverySummarySchema = z.object({
    /** M: propietarios con cuenta activa al publicar. */
    recipients: z.number().int().nonnegative(),
    /** N: correos que salieron. */
    sent: z.number().int().nonnegative(),
    /** Apagaron el correo de esta categoría (lo ven en la app). */
    suppressed: z.number().int().nonnegative(),
    /** La cuenta ya no estaba activa (o ya no tenía unidades en el
     *  proyecto) al enviar: no es un fallo de envío. */
    noAccount: z.number().int().nonnegative(),
    /** Se intentaron y no salieron, y ya no se reintentan (se cerró la
     *  ventana de 24 h, o se retiró el aviso después del fallo). */
    failed: z.number().int().nonnegative(),
    /** Nunca se intentaron porque el aviso se retiró antes: los detuvo el
     *  personal, no un fallo. */
    stopped: z.number().int().nonnegative(),
    /** Por enviar o por reintentar. */
    pending: z.number().int().nonnegative(),
});
export const announcementSchema = z.object({
    id: z.string().uuid(),
    assetGroupId: z.string().uuid(),
    category: announcementCategorySchema,
    title: z.string(),
    body: z.string(),
    status: announcementStatusSchema,
    /** La fecha programada (null = se envió al publicar). */
    scheduledFor: z.string().datetime().nullable(),
    /** Cuándo salió de verdad (null mientras está programado o si se canceló). */
    publishedAt: z.string().datetime().nullable(),
    retractedAt: z.string().datetime().nullable(),
    cancelledAt: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
    /** Ids (soft refs a auth_users): el dashboard resuelve el nombre con su
     *  roster, como en la bandeja de consultas. */
    createdBy: z.string().uuid(),
    retractedBy: z.string().uuid().nullable(),
    cancelledBy: z.string().uuid().nullable(),
    /** null salvo cancelado. */
    cancelReason: announcementCancelReasonSchema.nullable(),
    attachment: z.object({ sizeBytes: z.number().int().positive() }).nullable(),
    /** null mientras no se publica. */
    delivery: announcementDeliverySummarySchema.nullable(),
});
/** A quién le llegaría un aviso publicado ahora: propietarios con cuenta
 *  ACTIVA (un correo cada uno) y sus unidades (una fila de campana cada
 *  una). Un propietario invitado que nunca activó su cuenta no cuenta. */
export const announcementAudienceSchema = z.object({
    owners: z.number().int().nonnegative(),
    units: z.number().int().nonnegative(),
});
export const announcementListResponseSchema = z.object({
    /** Del más nuevo al más viejo (por creación). */
    announcements: z.array(announcementSchema),
    audience: announcementAudienceSchema,
    /** ¿Corre en este ambiente el job que publica los programados? Si no,
     *  sólo se puede enviar ahora (el servidor rechaza programar). */
    schedulingAvailable: z.boolean(),
});
/** «N de M entregados» (o «Sin propietarios con cuenta»). */
export function announcementDeliveryLabel(d) {
    if (d.recipients === 0)
        return "Sin propietarios con cuenta";
    return `${d.sent} de ${d.recipients} ${d.recipients === 1 ? "entregado" : "entregados"}`;
}
//# sourceMappingURL=announcements.js.map